import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

interface ManagedPropertySuccessProps {
  onViewManaged: () => void;
  onAddAnother: () => void;
}

const ManagedPropertySuccess: React.FC<ManagedPropertySuccessProps> = ({
  onViewManaged,
  onAddAnother,
}) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-10 max-w-2xl w-full text-center space-y-6"
      >
        <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto" />
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            Property added to your managed portfolio
          </h2>
          <p className="text-gray-600 mt-2">
            Great work. Keep momentum: review your managed properties or add another right away.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 text-left">
          {[
            {
              title: 'Stay organised',
              description: 'See rent status, notes, and tenancy progress in one dashboard.',
            },
            {
              title: 'Be compliance-ready',
              description:
                'Keep records of notices, rent reviews, and tenant comms for RTB/RPZ peace of mind.',
            },
            {
              title: 'Move faster',
              description:
                'Add tenants, send broadcasts, and track payments without jumping tools.',
            },
          ].map((item) => (
            <div
              key={item.title}
              className="rounded-xl border border-gray-100 bg-gray-50/70 p-4 shadow-sm"
            >
              <p className="font-semibold text-gray-900">{item.title}</p>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <button
            onClick={onViewManaged}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold shadow-md hover:bg-blue-700 transition"
          >
            View managed properties
          </button>
          <button
            onClick={onAddAnother}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-gray-300 text-gray-800 font-semibold bg-white hover:bg-gray-50 transition"
          >
            Add another managed property
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ManagedPropertySuccess;
